import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { getFirestore, doc, getDoc } from 'firebase/firestore';
import { useAuthContext } from '../../../../hooks/useAuthContext';
import { toast } from 'react-hot-toast';
import { GlobeAltIcon, UserIcon, CalendarIcon } from '@heroicons/react/24/outline';
import { INDUSTRY_OPTIONS, getIndustryByName, getCompanySizeByName, getShortNameByCompanySizeName } from '../../../../constants/companyData';
import CompleteProfileModal from './CompleteProfileModal';

const CompanyDetailPage = () => {
  const { companyId } = useParams();
  const navigate = useNavigate();
  const { user } = useAuthContext();
  
  // State management
  const [company, setCompany] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [isProcessing, setIsProcessing] = useState(false);
  const [isUndoing, setIsUndoing] = useState(false);
  const [isFastTracked, setIsFastTracked] = useState(false);
  const [fastTrackedCompanies, setFastTrackedCompanies] = useState({});
  const [isAdmin, setIsAdmin] = useState(false);
  
  // Complete Profile Modal states
  const [isCompleteProfileModalOpen, setIsCompleteProfileModalOpen] = useState(false);
  const [profileMissingItems, setProfileMissingItems] = useState([]);

  // Fetch company data
  const fetchCompany = async () => {
    try {
      setLoading(true);
      const db = getFirestore();
      const companyRef = doc(db, 'companies', companyId);
      const companySnap = await getDoc(companyRef);
      
      if (!companySnap.exists()) {
        setError('Company not found');
        return;
      }
      
      const companyData = { id: companySnap.id, ...companySnap.data() };
      setCompany(companyData);
    } catch (err) {
      console.error('Error fetching company:', err);
      setError('Failed to load company details. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  // Check if user is admin
  const checkAdminStatus = async () => {
    if (!user?.uid) return;
    
    try {
      const db = getFirestore();
      const userProfileRef = doc(db, 'user_profiles', user.uid);
      const userProfileSnap = await getDoc(userProfileRef);
      
      if (userProfileSnap.exists()) {
        const userData = userProfileSnap.data();
        setIsAdmin(userData.isAdmin === true);
      }
    } catch (err) {
      console.error('Error checking admin status:', err);
    }
  };

  // Fetch user's connection status for this company
  const fetchConnectionStatus = async () => {
    if (!user?.uid) return;
    
    try {
      const db = getFirestore();
      const userProfileRef = doc(db, 'user_profiles', user.uid);
      const userProfileSnap = await getDoc(userProfileRef);
      
      if (userProfileSnap.exists()) {
        const userData = userProfileSnap.data();
        if (userData.companyStatuses) {
          // Validate statuses against history to get the most recent status for each company
          const validatedStatuses = {};
          const companyStatusHistory = userData.companyStatusHistory || {};
          
          Object.entries(userData.companyStatuses).forEach(([cId, status]) => {
            // Check if there's a history for this company
            const history = companyStatusHistory[cId];
            if (history && Array.isArray(history) && history.length > 0) {
              // Get the most recent status from history
              const mostRecentEntry = history[history.length - 1];
              const mostRecentStatus = mostRecentEntry.status;
              
              // Only include active statuses (not null/cancelled)
              if (mostRecentStatus && (mostRecentStatus === 'introduction-requested' || mostRecentStatus === 'fast-tracked' || mostRecentStatus === 'contact_lead_assigned')) {
                validatedStatuses[cId] = mostRecentStatus;
              }
            } else {
              // If no history exists, use the current status if it's active
              if (status && (status === 'introduction-requested' || status === 'fast-tracked' || status === 'contact_lead_assigned')) {
                validatedStatuses[cId] = status;
              }
            }
          });
          
          setFastTrackedCompanies(validatedStatuses);
          setIsFastTracked(!!validatedStatuses[companyId]);
        }
      }
    } catch (err) {
      console.error('Error fetching connection status:', err);
    }
  };

  // Check if user's profile is complete for critical items (LinkedIn and Resume)
  const checkProfileCompleteness = async (userId) => {
    try {
      const db = getFirestore();
      const userProfileRef = doc(db, 'user_profiles', userId);
      const userProfileSnap = await getDoc(userProfileRef);
      
      if (!userProfileSnap.exists()) {
        return { isComplete: false, missingItems: ['linkedin', 'resume'] };
      }
      
      const userData = userProfileSnap.data();
      const missingItems = [];
      
      // Check LinkedIn
      if (!userData?.profileLinks?.linkedin) {
        missingItems.push('linkedin');
      }
      
      // Check Resume
      if (!userData?.resumeUrl && !userData?.resumeFileName) {
        missingItems.push('resume');
      }
      
      return {
        isComplete: missingItems.length === 0,
        missingItems
      };
    } catch (error) {
      console.error('Error checking profile completeness:', error);
      return { isComplete: false, missingItems: ['linkedin', 'resume'] };
    }
  };

  // Handle fast track request
  const handleFastTrack = async () => {
    if (!user?.uid || !company) return;

    // Check if this is the user's first connection request
    const activeConnectionCount = Object.values(fastTrackedCompanies).filter(status => 
      status === 'introduction-requested' || status === 'fast-tracked' || status === 'contact_lead_assigned'
    ).length;
    
    const isFirstRequest = activeConnectionCount === 0;
    
    // If this is the first request, check profile completeness
    if (isFirstRequest) {
      const profileCheck = await checkProfileCompleteness(user.uid);
      
      // If profile is incomplete and missing critical items, show modal
      if (!profileCheck.isComplete && profileCheck.missingItems.some(item => item === 'linkedin' || item === 'resume')) {
        setProfileMissingItems(profileCheck.missingItems);
        setIsCompleteProfileModalOpen(true);
        return; // Don't proceed with the connection yet
      }
    }
    
    // Proceed with the connection
    await proceedWithConnection();
  };

  // Actually proceed with the connection
  const proceedWithConnection = async () => {
    if (!user?.uid || !company) return;

    try {
      setIsProcessing(true);

      // Emit event that CompaniesTab listens to for actual connection logic
      const event = new CustomEvent('companyConnectionRequest', {
        detail: { companyId: company.id, userId: user.uid }
      });
      window.dispatchEvent(event);

      // Don't update local state optimistically - wait for the response from CompaniesTab
    } catch (error) {
      console.error('Error requesting introduction:', error);
      toast.error('Failed to request introduction. Please try again.');
      setIsProcessing(false);
    }
  };

  // Handle undo connection
  const handleUndo = async () => {
    if (!user?.uid || !company) return;

    try {
      setIsUndoing(true);

      // Emit event for undo
      const event = new CustomEvent('companyConnectionUndo', {
        detail: { companyId: company.id, userId: user.uid }
      });
      window.dispatchEvent(event);

      // Don't update local state optimistically - wait for the response from CompaniesTab
    } catch (error) {
      console.error('Error undoing connection:', error);
      toast.error('Failed to cancel request. Please try again.');
      setIsUndoing(false);
    }
  };

  // Handle Complete Profile Modal actions
  const handleCompleteProfileModalClose = () => {
    setIsCompleteProfileModalOpen(false);
    setProfileMissingItems([]);
  };

  const handleProceedAnyway = async () => {
    handleCompleteProfileModalClose();
    await proceedWithConnection();
  };

  // Format dates
  const formatDate = (dateValue) => {
    if (!dateValue) return 'N/A';
    
    try {
      let date;
      
      // Handle Firestore Timestamp objects
      if (dateValue && typeof dateValue.toDate === 'function') {
        date = dateValue.toDate();
      }
      // Handle Firestore seconds format
      else if (dateValue && dateValue.seconds) {
        date = new Date(dateValue.seconds * 1000);
      }
      // Handle regular date strings or Date objects
      else {
        date = new Date(dateValue);
      }
      
      // Check if date is valid
      if (isNaN(date.getTime())) {
        return 'N/A';
      }
      
      return date.toLocaleDateString('en-US', {
        year: 'numeric',
        month: 'long',
        day: 'numeric'
      });
    } catch {
      return 'N/A';
    }
  };

  // Initialize data on component mount
  useEffect(() => {
    if (companyId) {
      fetchCompany();
      checkAdminStatus();
      fetchConnectionStatus();
    }
  }, [companyId, user?.uid]);

  // Listen for connection status updates
  useEffect(() => {
    const handleConnectionUpdate = (event) => {
      const { companyId: updatedCompanyId, status } = event.detail;
      
      if (updatedCompanyId === companyId) {
        // Reset processing/undoing states
        setIsProcessing(false);
        setIsUndoing(false);
        
        if (status === null) {
          setIsFastTracked(false);
          setFastTrackedCompanies(prev => {
            const updated = { ...prev };
            delete updated[companyId];
            return updated;
          });
          toast.success('Connection request cancelled');
        } else {
          setIsFastTracked(true);
          setFastTrackedCompanies(prev => ({
            ...prev,
            [companyId]: status
          }));
          toast.success(`Introduction request sent for ${company?.name}!`);
        }
      }
    };

    const handleConnectionError = (event) => {
      const { companyId: errorCompanyId, error } = event.detail;
      
      if (errorCompanyId === companyId) {
        // Reset processing/undoing states
        setIsProcessing(false);
        setIsUndoing(false);
        toast.error(error || 'Failed to process request. Please try again.');
      }
    };
    
    window.addEventListener('connectionStatusUpdated', handleConnectionUpdate);
    window.addEventListener('connectionError', handleConnectionError);
    
    return () => {
      window.removeEventListener('connectionStatusUpdated', handleConnectionUpdate);
      window.removeEventListener('connectionError', handleConnectionError);
    };
  }, [companyId, company?.name]);

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 dark:bg-gray-900 flex items-center justify-center">
        <div className="inline-block h-12 w-12 animate-spin rounded-full border-4 border-solid border-purple-500 border-r-transparent dark:border-purple-400"></div>
      </div>
    );
  }

  if (error || !company) {
    return (
      <div className="min-h-screen bg-gray-50 dark:bg-gray-900 flex items-center justify-center">
        <div className="text-center">
          <h1 className="text-2xl font-bold text-gray-900 dark:text-gray-100 mb-4">
            {error || 'Company not found'}
          </h1>
          <button
            onClick={() => navigate('/dashboard')}
            className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md text-white bg-purple-600 hover:bg-purple-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-purple-500"
          >

            Back to Dashboard
          </button>
        </div>
      </div>
    );
  }

  // Get industry objects with short names for display
  const industryObjects = company.industries 
    ? company.industries.map(industryName => {
        const industryObj = getIndustryByName(industryName);
        return industryObj || { name: industryName, shortName: industryName };
      })
    : company.industry 
      ? [getIndustryByName(company.industry) || { name: company.industry, shortName: company.industry }]
      : [];

  // Get short name for company size
  const sizeShortName = company.size ? getShortNameByCompanySizeName(company.size) : '';

  return (
    <div className="bg-white dark:bg-gray-900">


      <main className="mx-auto mt-8 max-w-2xl px-4 pb-16 sm:px-6 sm:pb-24 lg:max-w-7xl lg:px-8">
        <div className="lg:grid lg:auto-rows-min lg:grid-cols-12 lg:gap-x-8">
          {/* Company information */}
          <div className="lg:col-span-5 lg:col-start-8">
            <div className="flex justify-between items-start">
              <h1 className="text-2xl font-bold text-gray-900 dark:text-gray-100">{company.name}</h1>
              {isFastTracked && (
                <span className="inline-flex items-center rounded-full bg-purple-100 px-3 py-1 text-sm font-medium text-purple-800 dark:bg-purple-900/50 dark:text-purple-200">
                  Introduction Requested
                </span>
              )}
            </div>

            {/* Company meta information */}
            <div className="mt-4 space-y-2">
              {company.size && (
                <div className="flex items-center text-sm text-gray-600 dark:text-gray-400">
                  <UserIcon className="h-4 w-4 mr-2" />
                  <span>{company.size}</span>
                </div>
              )}
              
              {company.website && (
                <div className="flex items-center text-sm">
                  <GlobeAltIcon className="h-4 w-4 mr-2 text-gray-600 dark:text-gray-400" />
                  <a
                    href={company.website.startsWith('http') ? company.website : `https://${company.website}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-purple-600 hover:text-purple-700 dark:text-purple-400 dark:hover:text-purple-300"
                  >
                    Visit Website
                  </a>
                </div>
              )}


            </div>

            {/* Industries */}
            {industryObjects.length > 0 && (
              <div className="mt-4">
                <h3 className="text-sm font-medium text-gray-900 dark:text-gray-100 mb-2">Industries</h3>
                <div className="flex flex-wrap gap-2">
                  {industryObjects.map((industry, index) => (
                    <span 
                      key={`${industry.name}-${index}`}
                      className="inline-flex items-center rounded-full bg-green-50 px-3 py-1 text-sm font-medium text-green-700 ring-1 ring-inset ring-green-600/20 dark:bg-green-900/30 dark:text-green-300 dark:ring-green-500/30"
                      title={industry.name}
                    >
                      {industry.shortName}
                    </span>
                  ))}
                </div>
              </div>
            )}
          </div>

          {/* Company logo */}
          <div className="mt-8 lg:col-span-7 lg:col-start-1 lg:row-span-3 lg:row-start-1 lg:mt-0">
            <div className="aspect-square w-full max-w-md mx-auto bg-gray-100 dark:bg-gray-800 rounded-lg flex items-center justify-center">
              {company.logo ? (
                <img 
                  src={company.logo} 
                  alt={`${company.name} logo`}
                  className="max-w-full max-h-full object-contain rounded-lg"
                />
              ) : (
                <span 
                  className="text-6xl font-bold text-purple-600 dark:text-purple-400" 
                  aria-hidden="true"
                >
                  {company.name.charAt(0)}
                </span>
              )}
            </div>
          </div>

          {/* Action button and description */}
          <div className="mt-8 lg:col-span-5">
            {/* Request Intro Button */}
            {isFastTracked ? (
              <div className="flex items-center justify-between p-4 bg-green-50 border border-green-200 rounded-lg dark:bg-green-900/30 dark:border-green-600">
                <div className="flex items-center">
                  <svg className="h-5 w-5 text-green-600 dark:text-green-400 mr-2" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                  </svg>
                  <span className="text-sm font-medium text-green-800 dark:text-green-200">
                    Introduction Requested
                  </span>
                </div>
                <button
                  onClick={handleUndo}
                  disabled={isUndoing}
                  className="text-sm font-medium text-green-700 hover:text-green-600 dark:text-green-300 dark:hover:text-green-200 disabled:opacity-50"
                >
                  {isUndoing ? 'Cancelling...' : 'Cancel Request'}
                </button>
              </div>
            ) : (
              <button
                onClick={handleFastTrack}
                disabled={isProcessing}
                className="w-full flex items-center justify-center rounded-md border border-transparent bg-purple-600 px-8 py-3 text-base font-medium text-white hover:bg-purple-700 focus:outline-none focus:ring-2 focus:ring-purple-500 focus:ring-offset-2 disabled:opacity-50 disabled:cursor-not-allowed dark:bg-purple-700 dark:hover:bg-purple-600"
              >
                {isProcessing ? (
                  <>
                    <svg className="animate-spin -ml-1 mr-3 h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                    </svg>
                    Requesting...
                  </>
                ) : (
                  'Request Intro'
                )}
              </button>
            )}

            {/* Company description */}
            {company.description && (
              <div className="mt-10">
                <h2 className="text-sm font-medium text-gray-900 dark:text-gray-100">About</h2>
                <div className="mt-4 text-sm text-gray-600 dark:text-gray-300 leading-relaxed">
                  {company.description}
                </div>
              </div>
            )}

            {/* Company details */}
            <div className="mt-8 border-t border-gray-200 dark:border-gray-700 pt-8">
              <h2 className="text-sm font-medium text-gray-900 dark:text-gray-100">Company Details</h2>

              <div className="mt-4 space-y-4">
                <dl className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                  {company.size && (
                    <div>
                      <dt className="text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wide">
                        Company Size
                      </dt>
                      <dd className="mt-1 text-sm text-gray-900 dark:text-gray-100">
                        {company.size}
                      </dd>
                    </div>
                  )}
                  

                </dl>
              </div>
            </div>
          </div>
        </div>
      </main>

      {/* Complete Profile Modal */}
      <CompleteProfileModal
        isOpen={isCompleteProfileModalOpen}
        onClose={handleCompleteProfileModalClose}
        onProceedAnyway={handleProceedAnyway}
        companyName={company?.name || ''}
        missingItems={profileMissingItems}
      />
    </div>
  );
};

export default CompanyDetailPage; 